import type { AppointmentRow } from '@pa/api/clinic'
import {
  Avatar,
  copyText,
  fmtDateTimeOf,
  fmtDayOf,
  fmtLongDate,
  fmtTime,
  relativeDay,
  timeAgo,
  useToast,
  zonedParts,
} from '@pa/ui'
import { Copy, ExternalLink, ShieldCheck, Stethoscope, TriangleAlert, Video } from 'lucide-react'
import { Fragment } from 'react'
import { OPEN_IN_PF, OPEN_IN_PF_COLUMN, OPENING } from '../model/openInPf'
import { CONFLICT_BADGE, CONFLICT_BADGE_NAME, hasConflicts } from '../model/conflicts'
import { pfMessageLabel, pfMessageShort } from '../model/pfMessage'

/**
 * "PF-5f981471-16a4-40bd-95fb-c5042d5457b5": the PF appointment ID (a GUID in real PF) as UserFlows C-01 / C-01a show
 * it ("PF appointment ID: PF-xxxx").
 */
export const pfRef = (row: Pick<AppointmentRow, 'pfAppointmentId'>) => `PF-${row.pfAppointmentId}`

/** How many characters of the PF appointment ID the C-01 rows show. */
export const PF_ID_SHORT = 8

/** "PF-5f981471…": the C-01 rows' short PF appointment ID (2026-10-16); the full one is its tooltip and name. */
export const shortPfRef = (row: Pick<AppointmentRow, 'pfAppointmentId'>) =>
  row.pfAppointmentId.length > PF_ID_SHORT ? `PF-${row.pfAppointmentId.slice(0, PF_ID_SHORT)}…` : pfRef(row)

export const COPY_PF_ID = 'Copy PF appointment ID'
export const PF_ID_COPIED = 'Copied'
export const PF_ID_COPY_FAILED = "Couldn't copy"

/** "Primary Care Center – Downtown" → "Downtown" (the mockup's `shortLocation`). */
export const shortLocation = (name: string): string => name.split(' – ').pop() ?? name

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** The visit type's icon: video for telehealth, otherwise a stethoscope (the mockup's demo icons aren't kept). */
export function TypeIcon({ telehealth, size }: { telehealth: boolean; size?: number }) {
  return telehealth ? <Video size={size} aria-hidden /> : <Stethoscope size={size} aria-hidden />
}

/** The slug as it came in; "Unknown" without one; "not in list" when it isn't a saved source (still counted). */
export function SourceName({ row }: { row: Pick<AppointmentRow, 'source' | 'sourceSaved'> }) {
  if (row.source === null) return <span className="src-unknown">Unknown</span>
  return (
    <>
      {row.source}
      {!row.sourceSaved && (
        <>
          {' '}
          <span className="src-unlisted" title="Not in your saved sources. Bookings are still counted.">
            not in list
          </span>
        </>
      )}
    </>
  )
}

const SourceCell = ({ row }: { row: AppointmentRow }) => (
  <span className="src-name">
    <i className="src-dot" aria-hidden /> <SourceName row={row} />
  </span>
)

function ProviderCell({ row, withLocation }: { row: AppointmentRow; withLocation?: boolean }) {
  return (
    <div className="row" style={{ gap: 8 }}>
      <Avatar name={row.provider.name} color={row.provider.color} size="sm" />
      <div>
        <b style={{ fontWeight: 600 }}>{row.provider.name}</b>
        {withLocation && <span className="cell-sub">{shortLocation(row.location.name)}</span>}
      </div>
    </div>
  )
}

const VisitCell = ({ row }: { row: AppointmentRow }) => (
  <div className="row" style={{ gap: 10 }}>
    <span
      className="kpi-icon"
      style={{ width: 30, height: 30, background: 'var(--blue-50)', color: 'var(--blue)' }}
      aria-hidden
    >
      <TypeIcon telehealth={row.visitType.telehealth} size={15} />
    </span>
    <div>
      <b style={{ fontWeight: 600 }}>{row.visitType.name}</b>
      <span className="cell-sub">{row.durationMinutes} min</span>
    </div>
  </div>
)

/** "Open in Practice Fusion" for one appointment (task 23): the row's `useOpenInPf`. */
export interface PfOpener {
  /** The appointment being opened now ("Opening…", disabled). */
  opening: string | null
  open: (appointmentId: string) => void
}

/**
 * The small "Open in Practice Fusion" icon button of a row or card (task 23). It never opens the drawer; the tooltip
 * is its `title`.
 */
export function OpenInPfIconButton({ row, pf }: { row: Pick<AppointmentRow, 'id'>; pf: PfOpener }) {
  const opening = pf.opening === row.id
  return (
    <button
      type="button"
      className="btn btn-ghost pf-go"
      aria-label={opening ? OPENING : OPEN_IN_PF}
      title={OPEN_IN_PF}
      disabled={opening}
      onClick={(e) => {
        e.stopPropagation()
        pf.open(row.id)
      }}
    >
      {opening ? <span className="spinner" aria-hidden /> : <ExternalLink aria-hidden />}
    </button>
  )
}

/** Copies "PF-{full id}" (as the drawer shows it) without opening the drawer; a toast says how it went. */
function CopyPfIdButton({ row }: { row: Pick<AppointmentRow, 'pfAppointmentId'> }) {
  const toast = useToast()
  return (
    <button
      type="button"
      className="btn btn-ghost pf-go pf-copy"
      aria-label={COPY_PF_ID}
      title={COPY_PF_ID}
      onClick={(e) => {
        e.stopPropagation()
        void copyText(pfRef(row))
          .catch(() => false)
          .then((ok) => {
            if (ok) toast(PF_ID_COPIED)
            else toast(PF_ID_COPY_FAILED, 'error')
          })
      }}
    >
      <Copy aria-hidden />
    </button>
  )
}

/**
 * The PF internal message's state under the ID (task 24's `pfMessage`; nothing without one): "Message sent" (green),
 * "Message pending" (amber), "Message failed" (red), "Message not sent" (grey); the drawer's words as the tooltip.
 */
export function PfMessageStatus({ row, timeZone }: { row: Pick<AppointmentRow, 'pfMessage'>; timeZone: string }) {
  if (!row.pfMessage) return null
  const { text, tone } = pfMessageShort(row.pfMessage)
  return (
    <span className={`pf-msg is-${tone}`} title={pfMessageLabel(row.pfMessage, timeZone)}>
      <span className={`state-dot sd-${tone}`} aria-hidden />
      {text}
    </span>
  )
}

/**
 * Task 26 (D-83): the warning-style "Conflict" badge of a row booked over something the provider had at another
 * location; nothing otherwise. Details are in C-01a.
 */
export function ConflictBadge({ row }: { row: Pick<AppointmentRow, 'conflicts'> }) {
  if (!hasConflicts(row)) return null
  return (
    <span
      className="tag tag-amber conflict-tag"
      role="img"
      aria-label={CONFLICT_BADGE_NAME}
      title={CONFLICT_BADGE_NAME}
    >
      <TriangleAlert aria-hidden />
      {CONFLICT_BADGE}
    </span>
  )
}

/**
 * The PF appointment ID cell: the short ID chip is also the row's keyboard way into C-01a (the row click is the mouse
 * shortcut; its name and tooltip carry the full ID), the copy button beside it copies the full ID, and the PF
 * message status and the conflict badge (task 26) sit under it.
 */
const PfCell = ({ row, onOpen, timeZone }: { row: AppointmentRow; onOpen: (id: string) => void; timeZone: string }) => (
  <td className="c-pf" data-label="PF appointment ID">
    <div className="pf-cell">
      <div className="pf-id-row">
        <button
          type="button"
          className="pf-ref pf-open"
          aria-label={`Open appointment ${pfRef(row)}`}
          title={pfRef(row)}
          onClick={(e) => {
            e.stopPropagation()
            onOpen(row.id)
          }}
        >
          <ShieldCheck aria-hidden /> {shortPfRef(row)}
        </button>
        <CopyPfIdButton row={row} />
      </div>
      <PfMessageStatus row={row} timeZone={timeZone} />
      <ConflictBadge row={row} />
    </div>
  </td>
)

/** The "Open in PF" column: the row's Open in Practice Fusion icon button. */
const OpenInPfCell = ({ row, pf }: { row: AppointmentRow; pf: PfOpener }) => (
  <td className="c-pfgo" data-label={OPEN_IN_PF_COLUMN}>
    <OpenInPfIconButton row={row} pf={pf} />
  </td>
)

interface TableProps {
  rows: AppointmentRow[]
  timeZone: string
  onOpen: (id: string) => void
  pf: PfOpener
}

/** "By booked date" (UserFlows C-01): newest booking first, as the API sorts it. */
export function BookedTable({ rows, timeZone, onOpen, pf }: TableProps) {
  return (
    <table className="table rtable bk-table">
      <thead>
        <tr>
          <th>Booked</th>
          <th>Appointment</th>
          <th>Visit type</th>
          <th>Provider</th>
          <th>Location</th>
          <th>Source</th>
          <th>PF appointment ID</th>
          <th>{OPEN_IN_PF_COLUMN}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          // The row click is a mouse shortcut: the PF appointment ID button is the keyboard way in.
          <tr
            key={a.id}
            className="clickable"
            onClick={() => {
              onOpen(a.id)
            }}
          >
            <td className="c-booked" style={{ whiteSpace: 'nowrap' }}>
              <b style={{ fontWeight: 600 }}>{fmtDateTimeOf(a.bookedAt, timeZone)}</b>
              <span className="cell-sub">{timeAgo(a.bookedAt)}</span>
            </td>
            <td className="c-when" data-label="Appointment" style={{ whiteSpace: 'nowrap' }}>
              <b style={{ fontWeight: 600 }}>{fmtDayOf(a.startAt, timeZone)}</b>
              <span className="cell-sub">{fmtTime(a.startAt, timeZone)}</span>
            </td>
            <td className="c-type" data-label="Visit type">
              <VisitCell row={a} />
            </td>
            <td className="c-provider" data-label="Provider">
              <ProviderCell row={a} />
            </td>
            <td className="c-location" data-label="Location">
              {shortLocation(a.location.name)}
            </td>
            <td className="c-source" data-label="Source">
              <SourceCell row={a} />
            </td>
            <PfCell row={a} onOpen={onOpen} timeZone={timeZone} />
            <OpenInPfCell row={a} pf={pf} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * "By appointment date" (UserFlows C-01): grouped by the visit's clinic-local day, with "Wednesday, October 15 ·
 * Today / Tomorrow / In N days · N appointments". The count is the day's full filtered total (`dayTotals`), so a day
 * split across pages still shows all of it.
 */
export function DayTable({
  rows,
  timeZone,
  onOpen,
  pf,
  dayTotals,
  today,
}: TableProps & { dayTotals: Record<string, number> | undefined; today: string }) {
  const groups = new Map<string, AppointmentRow[]>()
  for (const a of rows) groups.set(a.startDate, [...(groups.get(a.startDate) ?? []), a])
  return (
    <table className="table rtable appt-table">
      <thead>
        <tr>
          <th>Time</th>
          <th>Visit type</th>
          <th>Provider &amp; location</th>
          <th>Source</th>
          <th>Booked</th>
          <th>PF appointment ID</th>
          <th>{OPEN_IN_PF_COLUMN}</th>
        </tr>
      </thead>
      <tbody>
        {[...groups.entries()].map(([day, items]) => {
          const total = dayTotals?.[day] ?? items.length
          return (
            <Fragment key={day}>
              <tr className={`day-head ${day === today ? 'today' : ''} ${day < today ? 'past' : ''}`}>
                <td colSpan={7}>
                  <div className="day-head-inner">
                    <span className="day-tile" aria-hidden>
                      <span className="m">{MONTHS_SHORT[Number(day.slice(5, 7)) - 1]}</span>
                      <span className="d">{Number(day.slice(8, 10))}</span>
                    </span>
                    <div>
                      <b>{fmtLongDate(day)}</b>
                      <span>
                        <span className="rel">{relativeDay(day, today)}</span> · {total} appointment
                        {total === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
              {items.map((a) => (
                // The row click is a mouse shortcut: the PF appointment ID button is the keyboard way in.
                <tr
                  key={a.id}
                  className="day-row clickable"
                  onClick={() => {
                    onOpen(a.id)
                  }}
                >
                  <td className="time-cell c-time">
                    <b>{fmtTime(a.startAt, timeZone)}</b>
                    <span className="cell-sub">{a.durationMinutes} min</span>
                  </td>
                  <td className="c-type" data-label="Visit type">
                    <VisitCell row={a} />
                  </td>
                  <td className="c-provider" data-label="Provider">
                    <ProviderCell row={a} withLocation />
                  </td>
                  <td className="c-source" data-label="Source">
                    <SourceCell row={a} />
                  </td>
                  <td className="c-booked" data-label="Booked">
                    <span>{fmtDateTimeOf(a.bookedAt, timeZone)}</span>
                    <span className="cell-sub">{timeAgo(a.bookedAt)}</span>
                  </td>
                  <PfCell row={a} onOpen={onOpen} timeZone={timeZone} />
                  <OpenInPfCell row={a} pf={pf} />
                </tr>
              ))}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

/** "Wednesday, October 15 · 9:30 AM–10:00 AM" in clinic time (C-01a header). */
export function whenLine(row: Pick<AppointmentRow, 'startAt' | 'endAt'>, timeZone: string): string {
  const day = zonedParts(row.startAt, timeZone).date
  return `${fmtLongDate(day)} · ${fmtTime(row.startAt, timeZone)}–${fmtTime(row.endAt, timeZone)}`
}
